import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';

import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { formatStatus } from '@nxsys/utils';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import EditBankModal from './components/EditBankModal';
import { BankValues } from './components/BankForm';
import { Tag } from 'antd';
import { useUpdateCompanyById } from '@nxsys/hooks/api/company';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import CreateBankModal from './components/CreateBankModal';
import { showMessage } from '@nxsys/utils/message';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';


type Props = {
  data?: ICompany;
  fetchCompany: () => void;
};

const BankTab: React.FC<Props> = ({ data, fetchCompany }) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const banksList: IBank[] = data?.banksList ?? [];

  const { run: updateCompany } = useUpdateCompanyById();

  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [selectedBank, setSelectedBank] = useState<IBank | null>(null);
  const handleOpenEditModal = (bank: IBank) => {
    setSelectedBank(bank);
    setIsOpenEditModal(true);
  };

  const handleSubmitEditBank = async (values: BankValues) => {
    const editedBank = {
      bankName: values.bankName?.value ?? '',
      accountName: values.accountName,
      accountNumber: values.accountNumber,
      sortCode: values.sortCode,
      isPrimary: values.isPrimary,
      IBANReference: values.IBANReference ?? '',
      bankReference: values.bankReference ?? '',
      BIC: values.BIC ?? '',
      orgId: values.orgId ?? '',
    };
    const newBankList = banksList.map((bank) => {
      if (bank.id === selectedBank?.id) {
        return editedBank;
      }
      return {
        ...bank,
        isPrimary: editedBank.isPrimary ? false : bank.isPrimary,
      };
    });
    const params: UpdateCompanyByIdParams = {
      banksList: newBankList as IBank[],
    };
    await updateCompany(data?.id ?? '', params);
    showMessage({
      type: 'success',
      content: messages['common.updateSuccess'],
    });
    setIsOpenEditModal(false);
    setSelectedBank(null);
    fetchCompany();
  };

  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const handleSubmitCreateBank = async (values: BankValues) => {
    const newBank = {
      bankName: values.bankName?.value ?? '',
      accountName: values.accountName,
      accountNumber: values.accountNumber,
      sortCode: values.sortCode,
      isPrimary: values.isPrimary,
      IBANReference: values.IBANReference ?? '',
      bankReference: values.bankReference ?? '',
      BIC: values.BIC ?? '',
      orgId: values.orgId ?? '',
    };
    const newBankList = [...banksList, newBank];
    const params: UpdateCompanyByIdParams = {
      banksList: newBankList as IBank[],
    };
    await updateCompany(data?.id ?? '', params);
    showMessage({
      type: 'success',
      content: messages['common.addSuccess'],
    });
    setIsOpenCreateModal(false);
    fetchCompany();
  };

  const handleDeleteBank = async (item: IBank) => {
    if (banksList.length <= 1) {
      showMessage({
        type: 'error',
        content: messages['common.atLeastOneBankRequired'],
      });
      return;
    }
    if (item?.isPrimary) {
      showMessage({
        type: 'error',
        content: messages['common.primaryBankCannotBeDeleted'],
      });
      return;
    }
    AppConfirmationDelete.open({
      title: messages['common.delete'],
      paragraph: messages['common.deleteConfirm'],
      onConfirm: async () => {
        const newBankList = banksList.filter((bank) => bank.id !== item.id);
        const params: UpdateCompanyByIdParams = {
          banksList: newBankList as IBank[],
        };
        await updateCompany(data?.id ?? '', params);
        showMessage({
          type: 'success',
          content: messages['common.deleteSuccess'],
        });
        fetchCompany();
      },
    });
  };

  const irelandColumns: ColumnsType<IBank> = isIreland
    ? [
        {
          key: 'IBANReference',
          title: messages['common.IBANReference'],
          dataIndex: 'IBANReference',
          width: 200,
        },
        {
          key: 'bankReference',
          title: messages['common.bankReference'],
          dataIndex: 'bankReference',
          width: 200,
        },
        {
          key: 'BIC',
          title: messages['common.bic'],
          dataIndex: 'BIC',
          width: 150,
        },
        {
          key: 'orgId',
          title: 'Originator ID',
          dataIndex: 'orgId',
          width: 200,
        },
      ]
    : [];

  const columns: ColumnsType<IBank> = [
    {
      key: 'bankName',
      title: messages['common.bankName'],
      dataIndex: 'bankName',
      width: 250,
      render: (value: string, record: IBank) => (
        <Tag onClick={() => handleOpenEditModal(record)}>{value}</Tag>
      ),
    },
    {
      key: 'accountName',
      title: messages['common.accountName'],
      dataIndex: 'accountName',
      width: 250,
    },
    {
      key: 'accountNumber',
      title: messages['common.accountNumber8Digits'],
      dataIndex: 'accountNumber',
      width: 250,
    },
    {
      key: 'sortCode',
      title: messages['common.sortCode6Digits'],
      dataIndex: 'sortCode',
      width: 200,
    },
    ...irelandColumns,
    {
      key: 'isPrimary',
      title: null,
      dataIndex: 'isPrimary',
      align: 'center',
      render: (value: boolean) => (
        <AppTagStatus
          className={`tag-popup ${formatStatus(value ? messages['common.primary'] : messages['common.normal'])}`}
        >
          {formatStatus(
            value ? messages['common.primary'] : messages['common.normal'],
          )}
        </AppTagStatus>
      ),
      width: 150,
    },
    {
      key: 'action',
      title: null,
      align: 'left',
      render: (record: IBank) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => handleDeleteBank(record)}>
            <Icons.IconDelete />
          </li>
        </ul>
      ),
      width: 80,
    },
  ];

  return (
    <>
      <div className="p-[16px]">
        <div className="flex items-center gap-x-2 w-full justify-between mb-[16px]">
          <div />
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            onClick={() => setIsOpenCreateModal(true)}
          />
        </div>
        <AppsContent>
          <AppTableContainer
            data={banksList ?? []}
            loading={false}
            page={1}
            total={banksList.length}
            pageSize={banksList.length}
            pagination={false}
            scroll={{ x: isIreland ? 1950 : 800, y: 700 }}
            titleEmpty={messages['common.noData']}
            columns={columns}
          />
        </AppsContent>
      </div>
      {selectedBank && (
        <EditBankModal
          isOpen={isOpenEditModal}
          toggle={() => setIsOpenEditModal(false)}
          data={selectedBank}
          onSubmit={handleSubmitEditBank}
        />
      )}
      {isOpenCreateModal && (
        <CreateBankModal
          isOpen={isOpenCreateModal}
          toggle={() => setIsOpenCreateModal(false)}
          onSubmit={handleSubmitCreateBank}
        />
      )}
    </>
  );
};

export default BankTab;
